import { useCallback, useEffect, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface Attachment {
  project_key: string;
  project_name: string | null;
  board_id: number | null;
  board_name: string | null;
}

// Compact per-team project-attachment editor. Rendered inside the "expanded
// team" panel in ManageTeamsTab. Shows current attachments and lets an admin
// add a project (optionally scoped to a specific board) or remove one.
export default function TeamProjectsPanel({ teamId }: { teamId: number }) {
  const { showToast } = useToast();
  const [rows, setRows] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectKey, setProjectKey] = useState('');
  const [boardId, setBoardId] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService.getTeamProjects(teamId);
      if (r.success) setRows(r.data ?? []);
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Failed to load team projects');
    } finally {
      setLoading(false);
    }
  }, [teamId, showToast]);

  useEffect(() => { void load(); }, [load]);

  const attach = async () => {
    if (!projectKey.trim()) return;
    setAdding(true);
    try {
      const r = await apiService.attachTeamProject(teamId, {
        project_key: projectKey.trim(),
        board_id: boardId.trim() ? Number(boardId.trim()) : null,
      });
      if (r.success) {
        setProjectKey('');
        setBoardId('');
        await load();
        showToast('success', 'Project attached');
      } else {
        showToast('error', r.error || 'Failed to attach');
      }
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Failed to attach');
    } finally {
      setAdding(false);
    }
  };

  const detach = async (row: Attachment) => {
    try {
      const r = await apiService.detachTeamProject(teamId, row.project_key, row.board_id);
      if (r.success) {
        await load();
        showToast('success', 'Project detached');
      } else {
        showToast('error', r.error || 'Failed to detach');
      }
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Failed to detach');
    }
  };

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="text-xs font-semibold text-gray-700 mb-2">Projects &amp; boards</div>
      {loading ? (
        <div className="text-xs text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-gray-500 italic">No projects attached to this team.</div>
      ) : (
        <ul className="space-y-1 mb-2">
          {rows.map((r) => (
            <li key={`${r.project_key}:${r.board_id ?? 'none'}`} className="flex items-center justify-between text-xs bg-white border border-gray-200 rounded px-2 py-1">
              <span>
                <span className="font-mono">{r.project_key}</span>
                {r.project_name && <span className="text-gray-500 ml-1">({r.project_name})</span>}
                {r.board_id != null && (
                  <span className="text-gray-500 ml-2">
                    board {r.board_id}{r.board_name ? ` — ${r.board_name}` : ''}
                  </span>
                )}
              </span>
              <button onClick={() => detach(r)} className="text-gray-500 hover:text-red-600" title="Detach" aria-label={`Detach ${r.project_key}`}>
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input
          aria-label="Project key"
          className="w-24 px-2 py-1 text-xs border border-gray-300 rounded font-mono"
          placeholder="PROJECT"
          value={projectKey}
          onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
        />
        <input
          aria-label="Board id (optional)"
          className="w-20 px-2 py-1 text-xs border border-gray-300 rounded"
          placeholder="board id"
          value={boardId}
          onChange={(e) => setBoardId(e.target.value)}
        />
        <button
          onClick={attach}
          disabled={adding || !projectKey.trim()}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="h-3 w-3" /> Attach
        </button>
      </div>
    </div>
  );
}
